/**
 * Certification reimbursement claims: raise one, list them, decide them.
 *
 * ── Its own table, its own service ────────────────────────────────────────────────────────
 *
 * Nothing here touches the Expenses module. A claim lives in Certification_Reimbursements, is
 * approved through its own two fields, and links to the certification only by `certificationId`.
 * That isolation is the point: the certification flow can change without any of it having to be
 * safe for travel claims.
 *
 * ── Both decisions go through one function ────────────────────────────────────────────────
 *
 * `decide` handles all THREE stages — manager, certification admin, finance. Three functions
 * would have meant three copies of the ordering rule, and the copy that drifted would be the one
 * that let a claim skip a stage. "Mark as Reimbursed" is not a fourth operation: it is the
 * finance stage approving, which is the act of paying.
 */

import { generateClient } from 'aws-amplify/api';
import {
  CertificationReimbursement,
  financeStatusOf,
  ReimbursementApprovalStatus,
  ReimbursementDraft,
  ReimbursementStatus,
  deriveOverallStatus,
  reimbursementRetakeDeadline,
  validateReimbursementDraft,
} from '../types/certificationReimbursement';
import { canonicalizeEmail, sameIdentity } from './identity';
import { loadAllUsers, getFinanceAdminEmails } from './userLoader';
import { loadCertificationSettings } from './certificationSettings';
import {
  emailCertificationReimbursementSubmitted,
  emailCertificationReimbursementApproved,
  emailCertificationReimbursementRejected,
  emailCertificationReimbursementReimbursed,
} from './certificationEmails';
import {
  notifyCertificationReimbursementSubmitted,
  notifyCertificationReimbursementDecided,
} from './inAppNotification';
import { NotificationPriority } from '../types/notification';
import { CertificationsNotDeployedError, describeCertificationError } from './certificationService';

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const authMode = { authMode: 'userPool' as const };
const getData = (result: any): any => ('data' in result ? result.data : null);

const FIELDS = `
  id
  certificationId
  employeeEmail
  employeeName
  employeeId
  managerEmail
  certificationName
  provider
  examDate
  resultDate
  result
  retakeDeadline
  reimbursementAmount
  currency
  documentKey
  documentName
  notes
  managerApprovalStatus
  managerApprovedBy
  managerApprovedAt
  adminApprovalStatus
  adminApprovedBy
  adminApprovedAt
  financeApprovalStatus
  financeApprovedBy
  financeApprovedAt
  rejectionReason
  status
  createdAt
  updatedAt
`;

const listQuery = /* GraphQL */ `
  query ListCertificationReimbursements($limit: Int, $nextToken: String) {
    listCertificationReimbursements(limit: $limit, nextToken: $nextToken) {
      items { ${FIELDS} }
      nextToken
    }
  }
`;

const byEmployeeQuery = /* GraphQL */ `
  query CertificationReimbursementsByEmployee($employeeEmail: String!, $limit: Int) {
    certificationReimbursementsByEmployee(employeeEmail: $employeeEmail, limit: $limit) {
      items { ${FIELDS} }
      nextToken
    }
  }
`;

const createMutation = /* GraphQL */ `
  mutation CreateCertificationReimbursement($input: CreateCertificationReimbursementInput!) {
    createCertificationReimbursement(input: $input) { ${FIELDS} }
  }
`;

const updateMutation = /* GraphQL */ `
  mutation UpdateCertificationReimbursement($input: UpdateCertificationReimbursementInput!) {
    updateCertificationReimbursement(input: $input) { ${FIELDS} }
  }
`;

const fromRow = (row: any): CertificationReimbursement => ({
  ...row,
  employeeEmail: canonicalizeEmail(row?.employeeEmail),
  reimbursementAmount: Number(row?.reimbursementAmount) || 0,
  /*
   * The stored status is recomputed from the two approval fields on read.
   *
   * They are the facts; `status` is a derived convenience the queue sorts by. A row written by an
   * older build, or by a path that forgot to update it, would otherwise show a stale overall
   * state next to approval fields that contradict it — and the contradiction is invisible until
   * somebody notices a claim in the wrong queue.
   */
  status: deriveOverallStatus(
    row?.managerApprovalStatus,
    row?.adminApprovalStatus,
    row?.financeApprovalStatus || ReimbursementApprovalStatus.PENDING
  ),
});

export class CertificationReimbursementService {
  /** Every claim. For the approval queues. */
  static async listAll(limit = 200): Promise<CertificationReimbursement[]> {
    const items: CertificationReimbursement[] = [];
    let nextToken: string | undefined;
    let pages = 0;
    do {
      const result: any = await getClient().graphql({
        query: listQuery,
        variables: { limit, nextToken },
        ...authMode,
      });
      const page = getData(result)?.listCertificationReimbursements;
      // Null means no resolver is attached, not an empty table — see CertificationsNotDeployedError.
      if (!page && pages === 0) throw new CertificationsNotDeployedError();
      items.push(...(page?.items || []).filter(Boolean).map(fromRow));
      nextToken = page?.nextToken || undefined;
      pages += 1;
    } while (nextToken && pages < 25);
    return items;
  }

  /**
   * One employee's own claims.
   *
   * Through the byEmployee index, so an ordinary employee's page reads only their own rows
   * rather than pulling everybody's to the browser and hiding most of them.
   */
  static async listForEmployee(employeeEmail: string): Promise<CertificationReimbursement[]> {
    const email = canonicalizeEmail(employeeEmail);
    if (!email) return [];
    const result: any = await getClient().graphql({
      query: byEmployeeQuery,
      variables: { employeeEmail: email, limit: 200 },
      ...authMode,
    });
    const page = getData(result)?.certificationReimbursementsByEmployee;
    if (!page) throw new CertificationsNotDeployedError();
    return (page.items || []).filter(Boolean).map(fromRow);
  }

  /**
   * Raise a claim. It enters the flow already waiting on the manager.
   *
   * There is no draft state. A claim exists because somebody submitted it — an unsubmitted one is
   * an unfilled form, not a record, and a DRAFT status would be a state nothing ever leaves.
   */
  static async create(
    draft: ReimbursementDraft,
    employee: { email: string; name?: string; employeeId?: string; managerEmail?: string }
  ): Promise<CertificationReimbursement> {
    const problems = validateReimbursementDraft(draft);
    if (problems.length > 0) throw new Error(problems.join(' '));

    const input = {
      certificationId: draft.certificationId,
      employeeEmail: canonicalizeEmail(employee.email),
      employeeName: employee.name,
      employeeId: employee.employeeId,
      managerEmail: employee.managerEmail ? canonicalizeEmail(employee.managerEmail) : undefined,
      certificationName: draft.certificationName,
      provider: draft.provider,
      examDate: draft.examDate || undefined,
      resultDate: draft.resultDate,
      result: draft.result,
      retakeDeadline: reimbursementRetakeDeadline(draft.result, draft.resultDate),
      reimbursementAmount: Number(draft.reimbursementAmount),
      currency: draft.currency || 'INR',
      documentKey: draft.documentKey,
      documentName: draft.documentName,
      notes: draft.notes?.trim() || undefined,
      managerApprovalStatus: ReimbursementApprovalStatus.PENDING,
      adminApprovalStatus: ReimbursementApprovalStatus.PENDING,
      status: ReimbursementStatus.PENDING_MANAGER,
    };

    const result: any = await getClient().graphql({
      query: createMutation,
      variables: { input },
      ...authMode,
    });
    /* Null means the mutation has no resolver, exactly as on the read paths — say so rather than
       returning a claim object with no id for a caller to trip over later. */
    const row = getData(result)?.createCertificationReimbursement;
    if (!row) throw new CertificationsNotDeployedError();

    const claim = fromRow(row);
    await this.announce(claim);
    return claim;
  }

  /**
   * Tell whoever the claim is now waiting on.
   *
   * Here rather than at the call site, because the call site is precisely where it was
   * forgotten: the row has carried managerEmail since the day it was written and nothing
   * ever read it, so a manager’s only way of learning a claim existed was to open the
   * queue on the off-chance. Every other approval flow in this product announces itself;
   * this one silently did not. Putting it behind create() means a second caller cannot
   * reintroduce the silence.
   *
   * Never fails the claim. The row is saved by the time this runs, and a claim that exists
   * but went unannounced can be chased; one refused because an email bounced cannot.
   */
  private static async announce(claim: CertificationReimbursement): Promise<void> {
    try {
      /*
       * The manager, or the administrators when the claim skips that stage — a manager’s
       * own claim, or an employee with no manager set. Same branch the form uses to tell
       * the employee who will see it, so the promise on screen and the email agree.
       */
      const recipients = claim.managerEmail
        ? [claim.managerEmail]
        : await this.certificationAdminEmails();
      const reviewPath = claim.managerEmail
        ? '/certifications/approvals'
        : '/certifications/admin-approvals';
      if (recipients.length === 0) return;

      const who = claim.employeeName || claim.employeeEmail;
      await Promise.all([
        emailCertificationReimbursementSubmitted(recipients, {
          employeeName: who,
          certificationName: claim.certificationName,
          provider: claim.provider,
          amount: claim.reimbursementAmount,
          result: claim.result,
          reviewPath,
        }),
        notifyCertificationReimbursementSubmitted(
          recipients,
          who,
          claim.certificationName,
          claim.reimbursementAmount,
          reviewPath
        ),
      ]);
    } catch (error) {
      console.error(
        '[CertificationReimbursement] the claim was saved but could not be announced:',
        error
      );
    }
  }

  /**
   * Who can decide the admin stage: HR and Super Admins, plus the configured approver.
   *
   * Both, not either. Settings says in as many words that the configured person is "an
   * additional approver, not a replacement", so notifying only one of the two would leave
   * the people who can actually act on the claim unaware of it.
   */
  private static async certificationAdminEmails(): Promise<string[]> {
    const [settings, users] = await Promise.all([
      loadCertificationSettings(),
      loadAllUsers(),
    ]);

    /* Matches OVERSIGHT_ROLES in the certifications Lambda, which is what actually
       authorises the decision. Spelling normalised because roles arrive as "HR Admin",
       "HR_Admin" and "hr_admin" depending on where the row was written. */
    const oversight = ['hr_admin', 'super_admin'];
    const emails = users
      .filter(u =>
        oversight.includes(
          String(u.role || '')
            .toLowerCase()
            .replace(/[\s-]+/g, '_')
        )
      )
      .map(u => u.email);

    if (settings.certificationAdminEmail) emails.push(settings.certificationAdminEmail);

    /* The configured approver is frequently also an admin, and everyone holds two
       addresses across the domain move. */
    return this.dedupe(emails);
  }

  /**
   * Record one approver's decision.
   *
   * ── The ordering rule lives here ──────────────────────────────────────────────────────
   *
   * An admin cannot decide a claim the manager has not approved. Enforcing it in the service
   * rather than only by hiding buttons means the rule holds however the call is made — and the
   * queues are built from the same statuses, so a claim can never appear in two of them.
   *
   * A rejection sets the reason and ends the application; the other approver's field is left as
   * it was, because "the manager never saw it" and "the manager approved it" are different
   * histories and overwriting one with the other would lose that.
   */
  static async decide(
    claim: CertificationReimbursement,
    stage: 'manager' | 'admin' | 'finance',
    approve: boolean,
    actorEmail: string,
    reason?: string
  ): Promise<CertificationReimbursement> {
    if (!approve && !String(reason || '').trim()) {
      throw new Error('A rejection needs a reason explaining why.');
    }
    /*
     * The sequence, checked stage by stage. Each one requires the one before it to have said
     * yes — that is what makes the flow strictly ordered rather than three independent verdicts
     * that happen to be displayed in an order. The SERVER enforces the same three rules; this
     * copy exists so the UI can refuse without a round trip, not as the enforcement.
     */
    if (
      stage === 'admin' &&
      claim.managerApprovalStatus !== ReimbursementApprovalStatus.APPROVED
    ) {
      throw new Error(
        'This claim has not been approved by the manager yet, so it cannot be decided here.'
      );
    }
    if (stage === 'finance') {
      if (claim.managerApprovalStatus !== ReimbursementApprovalStatus.APPROVED) {
        throw new Error('This claim has not been approved by the manager yet.');
      }
      if (claim.adminApprovalStatus !== ReimbursementApprovalStatus.APPROVED) {
        throw new Error(
          'This claim has not been approved by the certification admin yet, so it cannot be reimbursed.'
        );
      }
    }
    if (claim.status === ReimbursementStatus.REJECTED) {
      throw new Error('This claim has already been rejected.');
    }
    if (claim.status === ReimbursementStatus.REIMBURSED) {
      throw new Error('This claim has already been reimbursed.');
    }

    const verdict = approve
      ? ReimbursementApprovalStatus.APPROVED
      : ReimbursementApprovalStatus.REJECTED;
    const now = new Date().toISOString();
    const actor = canonicalizeEmail(actorEmail);

    const managerStatus = stage === 'manager' ? verdict : claim.managerApprovalStatus;
    const adminStatus = stage === 'admin' ? verdict : claim.adminApprovalStatus;
    const financeStatus = stage === 'finance' ? verdict : financeStatusOf(claim);

    const input: Record<string, unknown> = {
      id: claim.id,
      status: deriveOverallStatus(managerStatus, adminStatus, financeStatus),
      // Explicitly null on approval, so a reason left from an earlier state cannot linger.
      rejectionReason: approve ? null : String(reason).trim(),
    };
    if (stage === 'manager') {
      input.managerApprovalStatus = verdict;
      input.managerApprovedBy = actor;
      input.managerApprovedAt = now;
    } else if (stage === 'admin') {
      input.adminApprovalStatus = verdict;
      input.adminApprovedBy = actor;
      input.adminApprovedAt = now;
    } else {
      input.financeApprovalStatus = verdict;
      input.financeApprovedBy = actor;
      input.financeApprovedAt = now;
    }

    const result: any = await getClient().graphql({
      query: updateMutation,
      variables: { input },
      ...authMode,
    });
    const row = getData(result)?.updateCertificationReimbursement;
    if (!row) throw new CertificationsNotDeployedError();

    const decided = fromRow(row);
    await this.announceDecision(decided, stage, approve, reason);
    return decided;
  }

  /**
   * Tell the employee, and whoever the claim has moved to.
   *
   * The employee hears about every step. "Still with the admin" and "rejected a week ago"
   * look identical from a screen that says nothing, and it is their money.
   *
   * Never fails the decision, for the reason announce() never fails the claim: the verdict is
   * already recorded, and refusing it because an email bounced would tell an approver their
   * decision had not registered when it had.
   */
  private static async announceDecision(
    claim: CertificationReimbursement,
    stage: 'manager' | 'admin' | 'finance',
    approve: boolean,
    reason?: string
  ): Promise<void> {
    try {
      const facts = {
        employeeName: claim.employeeName || claim.employeeEmail,
        certificationName: claim.certificationName,
        provider: claim.provider,
        result: claim.result,
        amount: claim.reimbursementAmount,
      };
      const employee = claim.employeeEmail ? [claim.employeeEmail] : [];

      /* Who acted, in the words the employee reading it would use. */
      const actor =
        stage === 'manager'
          ? 'Your manager'
          : stage === 'admin'
          ? 'The certification admin'
          : 'Finance';

      if (!approve) {
        await Promise.all([
          emailCertificationReimbursementRejected(employee, {
            ...facts,
            rejectedBy: actor,
            reason: String(reason || '').trim(),
          }),
          notifyCertificationReimbursementDecided(employee, {
            title: 'Certification Claim Rejected',
            message: `${actor} rejected your claim for "${claim.certificationName}"`,
            link: '/certifications/reimbursement',
            icon: '❌',
            priority: NotificationPriority.HIGH,
          }),
        ]);
        return;
      }

      if (stage === 'finance') {
        await Promise.all([
          emailCertificationReimbursementReimbursed(employee, facts),
          notifyCertificationReimbursementDecided(employee, {
            title: 'Certification Reimbursement Processed',
            message: `Your claim for "${claim.certificationName}" has been reimbursed`,
            link: '/certifications/reimbursement',
            icon: '💰',
          }),
        ]);
        return;
      }

      /*
       * An approval moves the claim on, so the next queue is told at the same moment the
       * employee is. One message to both: they need the same facts, and two wordings of the
       * same event is how the two drift apart.
       */
      const next =
        stage === 'manager'
          ? {
              recipients: await this.certificationAdminEmails(),
              path: '/certifications/admin-approvals',
              nextStep: 'It is now with the certification admin for the next approval.',
            }
          : {
              recipients: await getFinanceAdminEmails(),
              path: '/certifications/finance',
              nextStep: 'It is now with finance for payment.',
            };

      const recipients = this.dedupe([...employee, ...next.recipients]);
      await Promise.all([
        emailCertificationReimbursementApproved(recipients, {
          ...facts,
          approvedBy: actor,
          nextStep: next.nextStep,
          reviewPath: next.path,
        }),
        notifyCertificationReimbursementDecided(recipients, {
          title: 'Certification Claim Approved',
          message: `${actor} approved the claim for "${claim.certificationName}". ${next.nextStep}`,
          link: next.path,
          icon: '✅',
        }),
      ]);
    } catch (error) {
      console.error(
        '[CertificationReimbursement] the decision was recorded but could not be announced:',
        error
      );
    }
  }

  /**
   * One message per person.
   *
   * The same human holds two addresses across the domain move, and is frequently both an
   * approver and the employee on somebody else’s claim.
   */
  private static dedupe(emails: string[]): string[] {
    const seen = new Set<string>();
    return emails.filter(email => {
      const key = canonicalizeEmail(email);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * Move a claim onto a later attempt's result, after a retake.
   *
   * The claim's denormalised copy has to follow the certification, or the queue would keep
   * showing "Failed" beside a certification that now reads Passed. The approval fields are NOT
   * touched: a decision already made about paying is not undone by a later exam sitting.
   */
  static async syncToRetake(
    claim: CertificationReimbursement,
    updated: { result: CertificationReimbursement['result']; resultDate: string; documentKey?: string; documentName?: string }
  ): Promise<CertificationReimbursement> {
    const result: any = await getClient().graphql({
      query: updateMutation,
      variables: {
        input: {
          id: claim.id,
          result: updated.result,
          resultDate: updated.resultDate,
          retakeDeadline: reimbursementRetakeDeadline(updated.result, updated.resultDate) || null,
          documentKey: updated.documentKey,
          documentName: updated.documentName,
        },
      },
      ...authMode,
    });
    const row = getData(result)?.updateCertificationReimbursement;
    if (!row) throw new CertificationsNotDeployedError();
    return fromRow(row);
  }
}

/** Claims a given manager is responsible for. Matched on identity, not on a raw string. */
export function claimsForManager(
  claims: CertificationReimbursement[],
  managerEmail?: string
): CertificationReimbursement[] {
  if (!managerEmail) return [];
  return claims.filter((c) => sameIdentity(c.managerEmail, managerEmail));
}

export { describeCertificationError };
